import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ManageCookiesLink } from "../components/CookieConsent";
import {
  ArrowLeft,
  CalendarDays,
  MapPin,
  Loader2,
  Ticket,
  CheckCircle2,
  Timer,
  Copy,
  CreditCard,
  XCircle,
  Info,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { checkoutApi, formatAuDate, centsToAUD, ticketPdfUrl } from "../lib/events";
import { extractError } from "../lib/api";
import { pingCrossSellConversion } from "../lib/crosssell";
import { Logo } from "../components/Logo";

function useCountdown(expiresAtIso) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAtIso) return null;
  const diffSec = Math.max(0, Math.floor((new Date(expiresAtIso).getTime() - now) / 1000));
  const m = Math.floor(diffSec / 60);
  const s = diffSec % 60;
  return { totalSec: diffSec, label: `${m}:${String(s).padStart(2, "0")}` };
}

function TicketStub({ t, event }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-[1fr_auto] border-2 border-[var(--gt-ink)] rounded-sm bg-white overflow-hidden shadow-[6px_6px_0px_0px_var(--gt-ink)]"
      data-testid={`ticket-${t.code}`}
    >
      <div className="p-6">
        <div className="gt-label mb-2">Ticket</div>
        <div className="font-heading text-2xl font-black tracking-tight leading-tight mb-1">
          {t.ticket_type_name}
        </div>
        <div className="text-xs text-[var(--gt-ink-2)] mb-5">
          {event?.title} · {formatAuDate(event?.event_start_at || event?.start_at, event?.event_timezone || event?.timezone)}
        </div>
        {t.attendee_name && (
          <div className="mb-4" data-testid={`ticket-attendee-${t.code}`}>
            <div className="gt-label mb-1">Attendee</div>
            <div className="text-sm font-semibold">{t.attendee_name}</div>
          </div>
        )}
        {t.seat_label && (
          <div className="mb-4 -mx-1 inline-flex items-center gap-2 px-2 py-1 bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)] rounded-sm" data-testid={`ticket-seat-${t.code}`}>
            <span className="text-[10px] uppercase tracking-widest opacity-70">Seat</span>
            <span className="font-mono font-bold text-sm">
              {t.section ? `${t.section} · ` : ""}{t.row_label ? `Row ${t.row_label} · ` : ""}{t.seat_label}
            </span>
          </div>
        )}
        <div className="gt-label mb-1">Code</div>
        <div className="font-mono text-lg font-bold" data-testid={`ticket-code-${t.code}`}>
          {t.code}
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
          {t.price_cents === 0 ? "Complimentary · AUD $0" : centsToAUD(t.price_cents)}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={`/t/${t.code}`}
            className="gt-btn-ghost text-xs"
            data-testid={`open-ticket-${t.code}`}
          >
            Open ticket
          </Link>
          <a
            href={ticketPdfUrl(t.code)}
            target="_blank"
            rel="noreferrer"
            className="gt-btn-ghost text-xs"
            data-testid={`download-pdf-${t.code}`}
          >
            Download PDF
          </a>
        </div>
      </div>
      <div className="bg-white border-t-2 sm:border-t-0 sm:border-l-2 border-[var(--gt-ink)] p-6 flex flex-col items-center justify-center">
        <QRCodeSVG value={t.code} size={112} bgColor="#ffffff" fgColor="#09090b" level="M" />
        <div className="mt-2 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
          Scan at door
        </div>
      </div>
    </div>
  );
}

export default function Checkout() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnStatus = searchParams.get("status"); // 'success' | 'cancelled' | null

  const [session, setSession] = useState(null);
  const [order, setOrder] = useState(null);
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [pollElapsedSec, setPollElapsedSec] = useState(0);
  const [manualChecking, setManualChecking] = useState(false);
  const [manualReview, setManualReview] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consented, setConsented] = useState(false);
  // Configurable Additional Guest Details — one record per ticket unit.
  const [attendees, setAttendees] = useState([]);
  const [attendeeErrors, setAttendeeErrors] = useState({});
  const pollIntervalRef = useRef(null);
  const pollElapsedRef = useRef(null);
  const submitLockRef = useRef(false);  // hard client-side double-tap guard

  const countdown = useCountdown(session?.expires_at);
  const isFree = session?.amount_total_cents === 0;
  const isPaid = session && session.amount_total_cents > 0;

  // Configurable Additional Guest Details — resolve config + flatten ticket units.
  const guestConfig = session?.guest_details_config || { name: "off", email: "off", phone: "off" };
  const guestOn = guestConfig.name !== "off" || guestConfig.email !== "off" || guestConfig.phone !== "off";
  const guestUnits = useMemo(() => {
    const units = [];
    (session?.items || []).forEach((it) => {
      for (let i = 0; i < (it.quantity || 0); i++) {
        units.push({ ticket_type_id: it.ticket_type_id, ticket_type_name: it.ticket_type_name });
      }
    });
    return units;
  }, [session]);

  useEffect(() => {
    if (!guestOn) { setAttendees([]); return; }
    setAttendees((prev) => guestUnits.map((u, i) => prev[i] || { name: "", email: "", phone: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestUnits.length, guestOn]);

  const setAttendeeField = (i, k, v) => {
    setAttendees((p) => p.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)));
    setAttendeeErrors((e) => {
      if (!e[`${i}-${k}`]) return e;
      const next = { ...e };
      delete next[`${i}-${k}`];
      return next;
    });
  };
  const copyBuyerToAttendee = (i) =>
    setAttendees((p) => p.map((a, idx) => (idx === i ? { name, email, phone } : a)));

  // Sprint AD — consent-gated, fire-and-forget cross-sell attribution ping
  useEffect(() => {
    if (order?.id) pingCrossSellConversion(order.id);
  }, [order?.id]);

  useEffect(() => {
    let alive = true;
    checkoutApi
      .get(sessionId)
      .then((s) => alive && setSession(s))
      .catch((e) => alive && setError(extractError(e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [sessionId]);

  // If we came back from Stripe with status=success, poll the server-side
  // status endpoint. That endpoint checks Stripe directly and idempotently
  // finalizes the order (race-safe with the webhook) — so buyers never get
  // stuck waiting for the webhook to arrive.
  useEffect(() => {
    if (returnStatus !== "success" || !session || order) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 30; // 30 × 3s = 90s of automatic polling
    const stopAll = () => {
      if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      if (pollElapsedRef.current) { clearInterval(pollElapsedRef.current); pollElapsedRef.current = null; }
    };

    setPolling(true);
    setPollTimedOut(false);
    setPollElapsedSec(0);
    pollElapsedRef.current = setInterval(() => setPollElapsedSec((s) => s + 1), 1000);

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const res = await checkoutApi.status(sessionId);
        if (cancelled) return;
        if (res.state === "paid" && res.order) {
          stopAll();
          setPolling(false);
          // Redirect to the secure Order Confirmation Hub
          if (res.order.id && res.order.access_token) {
            navigate(`/order/${res.order.id}?token=${encodeURIComponent(res.order.access_token)}`, { replace: true });
          } else {
            setOrder(res.order);
          }
          return;
        }
        if (res.state === "failed" || res.state === "expired") {
          setError(res.state === "expired"
            ? "Your checkout expired before payment completed. Please start again."
            : "Payment did not complete. Please try again.");
          setPolling(false);
          stopAll();
          return;
        }
        if (res.state === "manual_review") {
          // Payment received but held for confirmation — never auto-issue tickets.
          setManualReview(true);
          setPolling(false);
          stopAll();
          return;
        }
        // state === 'processing' → keep polling
      } catch (e) {
        // 409 = validation mismatch, terminal
        if (e?.response?.status === 409) {
          setError(extractError(e));
          setPolling(false);
          stopAll();
          return;
        }
        // transient — swallow and retry
      }
      if (attempts >= maxAttempts) {
        setPollTimedOut(true);
        setPolling(false);
        stopAll();
      }
    };

    poll();
    pollIntervalRef.current = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      stopAll();
    };
  }, [returnStatus, session?.id, sessionId, order]);

  const onManualCheck = async () => {
    setManualChecking(true);
    setError("");
    try {
      const res = await checkoutApi.status(sessionId);
      if (res.state === "paid" && res.order) {
        setPollTimedOut(false);
        if (res.order.id && res.order.access_token) {
          navigate(`/order/${res.order.id}?token=${encodeURIComponent(res.order.access_token)}`, { replace: true });
        } else {
          setOrder(res.order);
        }
      } else if (res.state === "failed" || res.state === "expired") {
        setError(res.state === "expired"
          ? "Your checkout expired before payment completed."
          : "Payment did not complete.");
      } else if (res.state === "manual_review") {
        setManualReview(true);
      } else {
        setError("Stripe hasn't confirmed the payment yet. Please try again in a few seconds.");
      }
    } catch (e) {
      setError(extractError(e));
    } finally {
      setManualChecking(false);
    }
  };

  useEffect(() => {
    if (returnStatus === "cancelled" && session?.status === "pending") {
      setError("Payment was cancelled. You can update your details and try again.");
    }
  }, [returnStatus, session?.status]);

  useEffect(() => {
    if (!countdown || order) return;
    if (countdown.totalSec === 0 && session?.status === "pending") {
      setError("Your checkout has expired. Please start again.");
    }
  }, [countdown, session, order]);

  const onComplete = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;  // ignore rapid re-taps
    setError("");
    if (!consented) { setError("Please accept the terms & privacy policy."); return; }
    if (!name.trim() || !email.trim()) { setError("Name and email are required."); return; }
    if (!phone.trim()) { setError("Please enter a phone number."); return; }

    // Configurable Additional Guest Details — client-side required checks + payload.
    let attendeesPayload;
    if (guestOn) {
      const errs = {};
      guestUnits.forEach((u, i) => {
        const a = attendees[i] || {};
        if (guestConfig.name === "required" && !(a.name || "").trim()) errs[`${i}-name`] = "Required";
        if (guestConfig.email === "required" && !(a.email || "").trim()) errs[`${i}-email`] = "Required";
        if (guestConfig.phone === "required" && !(a.phone || "").trim()) errs[`${i}-phone`] = "Required";
      });
      if (Object.keys(errs).length) {
        setAttendeeErrors(errs);
        setError("Please complete the required guest details for each ticket.");
        return;
      }
      setAttendeeErrors({});
      attendeesPayload = guestUnits.map((u, i) => ({
        ticket_type_id: u.ticket_type_id,
        name: (attendees[i]?.name || "").trim() || null,
        email: (attendees[i]?.email || "").trim() || null,
        phone: (attendees[i]?.phone || "").trim() || null,
      }));
    }

    submitLockRef.current = true;
    setSubmitting(true);
    try {
      const result = await checkoutApi.complete(sessionId, {
        buyer_name: name.trim(),
        buyer_email: email.trim(),
        buyer_phone: phone.trim(),
        ...(attendeesPayload ? { attendees: attendeesPayload } : {}),
        consented,
        origin: window.location.origin,
      });
      // Paid path — redirect to Stripe
      if (result.redirect_url) {
        window.location.href = result.redirect_url;
        return;  // keep the lock engaged during navigation
      }
      // Free path — order returned inline. Redirect to the secure Order Hub.
      if (result.id && result.access_token) {
        navigate(`/order/${result.id}?token=${encodeURIComponent(result.access_token)}`, { replace: true });
        return;
      }
      // Fallback (shouldn't happen)
      setOrder(result);
      toast.success("Order confirmed. Your tickets are ready.");
    } catch (err) {
      setError(extractError(err));
      submitLockRef.current = false;  // release lock on error so retry works
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = async () => {
    try { await checkoutApi.cancel(sessionId); } catch (_) { /* ignore */ }
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="checkout-loading">
        <Loader2 className="animate-spin text-[var(--gt-ink-2)]" size={20} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--gt-bg-marketing)]" data-testid="checkout-page">
      <a href="#checkout-main" className="gt-skip-link" data-testid="skip-to-content">Skip to main content</a>
      <nav className="gt-glass-nav">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Logo size="md" />
          <Link to="/" className="gt-btn-ghost text-sm" data-testid="checkout-home">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-16" id="checkout-main">
        {order ? (
          /* ---------------------------- Confirmation ---------------------------- */
          <div data-testid="checkout-confirmation">
            <div className="mb-10 text-center">
              <CheckCircle2 size={48} className="mx-auto text-[var(--gt-accent)] mb-4" strokeWidth={1.5} />
              <div className="gt-label mb-2">Order confirmed</div>
              <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
                You&apos;re going.
              </h1>
              <p className="text-[var(--gt-ink-2)] max-w-lg mx-auto">
                We&apos;ve reserved your {order.tickets.length} ticket{order.tickets.length === 1 ? "" : "s"} for{" "}
                <span className="font-semibold text-[var(--gt-ink)]">{session?.event_title}</span>. Save this page or take a screenshot — you&apos;ll present the QR at the door.
              </p>
            </div>

            <div className="gt-card mb-8" data-testid="order-summary">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div>
                  <div className="gt-label mb-1">Order reference</div>
                  <div className="font-mono text-sm break-all" data-testid="order-id">{order.id}</div>
                </div>
                <div className="text-right">
                  <div className="gt-label mb-1">Amount paid</div>
                  <div className="font-heading text-2xl font-black tracking-tight" data-testid="order-total">
                    {order.amount_total_cents === 0 ? "A$0.00" : centsToAUD(order.amount_total_cents)}
                  </div>
                </div>
              </div>
              {order.amount_total_cents > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs border-y border-[var(--gt-line)] py-3 mb-3">
                  <div>
                    <div className="gt-label mb-0.5">Ticket subtotal</div>
                    <div className="font-mono">{centsToAUD(order.gross_ticket_cents)}</div>
                  </div>
                  <div>
                    <div className="gt-label mb-0.5">Service fee</div>
                    <div className="font-mono" data-testid="order-service-charge">
                      {(order.service_charge_paid_by || (order.fee_mode === "passed_on" ? "customer" : "included")) === "customer"
                        ? centsToAUD(order.service_charge_cents ?? order.fee_cents ?? 0)
                        : "Included in ticket price"}
                    </div>
                  </div>
                  <div>
                    <div className="gt-label mb-0.5">Service fee GST</div>
                    <div className="font-mono" data-testid="order-gst">
                      {order.gst_enabled === false || order.gst_registered_snapshot === false
                        ? "Not applied"
                        : (order.gst_paid_by || order.tax_paid_by || "customer") === "customer"
                          ? centsToAUD(order.gst_amount_cents ?? order.gst_cents ?? 0)
                          : "Included in ticket price"}
                    </div>
                  </div>
                  <div>
                    <div className="gt-label mb-0.5">Payment</div>
                    <div className="font-mono text-[10px] break-all">
                      {order.stripe_session_id ? order.stripe_session_id.slice(0, 14) + "…" : "Free"}
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm border-t border-[var(--gt-line)] pt-4">
                <div>
                  <div className="gt-label mb-1">Ticket holder</div>
                  <div>{order.buyer_name}</div>
                  <div className="text-[var(--gt-ink-2)] text-xs">{order.buyer_email}</div>
                </div>
                <div>
                  <div className="gt-label mb-1">Event</div>
                  <div className="font-semibold">{session?.event_title}</div>
                  <div className="text-[var(--gt-ink-2)] text-xs">
                    {formatAuDate(session?.event_start_at, session?.event_timezone)} · {session?.event_venue_name}, {session?.event_state}
                  </div>
                </div>
              </div>
              {/* Sprint T1 — purchased extras */}
              {(order.addon_items || []).length > 0 && (
                <div className="border-t border-[var(--gt-line)] pt-4 mt-4" data-testid="order-extras">
                  <div className="gt-label mb-2">Extras</div>
                  <div className="space-y-1.5">
                    {order.addon_items.map((a) => (
                      <div key={a.addon_id} className="flex items-center justify-between text-sm" data-testid={`order-extra-${a.addon_id}`}>
                        <span>{a.name} <span className="text-xs text-[var(--gt-ink-2)]">× {a.quantity}</span></span>
                        <span className="font-mono font-bold">{a.line_total_cents === 0 ? "Free" : centsToAUD(a.line_total_cents)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
              <div className="gt-label">Your tickets</div>
              <button
                type="button"
                onClick={() => {
                  const link = window.location.href;
                  navigator.clipboard.writeText(link);
                  toast.success("Confirmation link copied");
                }}
                className="gt-btn-ghost text-xs"
                data-testid="copy-confirmation-btn"
              >
                <Copy size={12} /> Copy link
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="tickets-grid">
              {order.tickets.map((t) => (
                <TicketStub key={t.id} t={t} event={session} />
              ))}
            </div>

            <div className="mt-10 text-xs text-[var(--gt-ink-3)] text-center leading-relaxed">
              Confirmation sent to <span className="text-[var(--gt-ink-2)]">{order.buyer_email}</span>.
              {" "}Lost it? <Link to="/resend" className="underline underline-offset-2 text-[var(--gt-ink-2)]" data-testid="link-to-resend">Resend anytime</Link>.
            </div>

            {order.dev_email_preview_url && (
              <div
                className="mt-4 border-2 border-dashed border-[var(--gt-ink-2)] bg-white px-4 py-3 text-xs text-[var(--gt-ink-2)] flex flex-wrap items-center justify-between gap-2"
                data-testid="dev-email-preview-banner"
              >
                <span className="uppercase tracking-widest text-[10px] text-[var(--gt-ink-3)]">Dev preview</span>
                <a
                  href={order.dev_email_preview_url}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-2 font-mono break-all"
                  data-testid="dev-email-preview-link"
                >
                  Open confirmation email
                </a>
              </div>
            )}
          </div>
        ) : manualReview ? (
          /* ---------------------------- Payment received — manual review ---------------------------- */
          <div className="text-center py-16" data-testid="checkout-manual-review">
            <ShieldCheck size={40} className="mx-auto text-[var(--gt-accent)] mb-6" strokeWidth={1.5} />
            <div className="gt-label mb-2">Payment received</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
              Your booking is being confirmed.
            </h1>
            <p className="text-[var(--gt-ink-2)] max-w-md mx-auto mb-6" data-testid="manual-review-message">
              Your payment has been received and your booking is being confirmed.
              Please do not make another payment. Our support team will contact you
              if any action is required.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/resend" className="gt-btn-ghost text-sm" data-testid="manual-review-resend">
                <Mail size={14} /> Check my email for tickets
              </Link>
            </div>
          </div>
        ) : polling ? (
          /* ---------------------------- Payment polling ---------------------------- */
          <div className="text-center py-20" data-testid="checkout-polling">
            <Loader2 size={40} className="mx-auto animate-spin text-[var(--gt-accent)] mb-6" strokeWidth={1.5} />
            <div className="gt-label mb-2">Payment processing</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
              Confirming your payment…
            </h1>
            <p className="text-[var(--gt-ink-2)] max-w-md mx-auto">
              Stripe is confirming your card. This usually takes a few seconds — please don&apos;t close this tab.
            </p>
            <p className="mt-4 text-xs text-[var(--gt-ink-3)]" data-testid="polling-elapsed">
              {pollElapsedSec}s elapsed
            </p>
          </div>
        ) : pollTimedOut ? (
          /* ---------------------------- Poll timed out ---------------------------- */
          <div className="text-center py-16" data-testid="checkout-poll-timeout">
            <Timer size={40} className="mx-auto text-[var(--gt-ink-2)] mb-6" strokeWidth={1.5} />
            <div className="gt-label mb-2">Still waiting on Stripe</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
              Payment is taking longer than usual.
            </h1>
            <p className="text-[var(--gt-ink-2)] max-w-md mx-auto mb-6">
              Your card may still be authorising. Click below to check now — if it went through, you&apos;ll also receive your tickets by email.
            </p>
            {error && (
              <div className="text-sm text-[var(--gt-accent)] font-medium mb-4" data-testid="poll-timeout-error" role="alert">
                {error}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={onManualCheck}
                className="gt-btn-primary"
                disabled={manualChecking}
                data-testid="manual-check-status-btn"
              >
                {manualChecking && <Loader2 size={14} className="animate-spin" />}
                Check payment status
              </button>
              <Link to="/resend" className="gt-btn-ghost text-sm" data-testid="poll-timeout-resend">
                <Mail size={14} /> Resend to my email
              </Link>
            </div>
          </div>
        ) : (
          /* ---------------------------- Form ---------------------------- */
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10">
            <div>
              <div className="gt-label mb-3">Guest checkout</div>
              <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-3">
                Almost there.
              </h1>
              <p className="text-[var(--gt-ink-2)] mb-8">
                No account needed. We&apos;ll issue your tickets straight away.
              </p>

              {countdown && countdown.totalSec > 0 && session?.status === "pending" && (
                <div className="mb-6 inline-flex items-center gap-2 border border-[var(--gt-line)] px-3 py-2 rounded-sm bg-white" data-testid="checkout-countdown">
                  <Timer size={14} className="text-[var(--gt-accent)]" />
                  <span className="text-sm">Your tickets are on hold for </span>
                  <span className="font-mono font-bold text-sm" data-testid="countdown-label">{countdown.label}</span>
                </div>
              )}

              <form onSubmit={onComplete} className="gt-card space-y-5" data-testid="checkout-form">
                <div>
                  <label className="gt-form-label">Full name</label>
                  <input
                    className="gt-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Kelly"
                    required
                    autoComplete="name"
                    data-testid="buyer-name-input"
                  />
                </div>
                <div>
                  <label className="gt-form-label">Email address</label>
                  <input
                    className="gt-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@youraddress.com.au"
                    required
                    autoComplete="email"
                    inputMode="email"
                    data-testid="buyer-email-input"
                  />
                  <p className="mt-2 text-xs text-[var(--gt-ink-3)]">
                    Your tickets will be sent here as a downloadable PDF.
                  </p>
                </div>
                <div>
                  <label className="gt-form-label">Phone number</label>
                  <input
                    className="gt-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0412 345 678"
                    required
                    autoComplete="tel"
                    inputMode="tel"
                    pattern="[\d\s+()\-]{6,32}"
                    title="Please enter a phone number (Australian or international format)."
                    data-testid="buyer-phone-input"
                  />
                  <p className="mt-2 text-xs text-[var(--gt-ink-3)]">
                    For urgent event updates only. Never used for marketing.
                  </p>
                </div>

                {guestOn && guestUnits.length > 0 && (
                  <div className="border-t border-[var(--gt-line)] pt-5 space-y-4" data-testid="attendee-section">
                    <div>
                      <div className="gt-form-label">Guest details</div>
                      <p className="text-xs text-[var(--gt-ink-3)]">
                        Tell us who&apos;s attending on each ticket.
                      </p>
                    </div>
                    {guestUnits.map((u, i) => (
                      <div
                        key={i}
                        className="border border-[var(--gt-line)] rounded-sm p-4 bg-[var(--gt-bg-surface)]"
                        data-testid={`attendee-card-${i}`}
                      >
                        <div className="flex items-center justify-between mb-3 gap-2">
                          <div className="text-[11px] font-semibold uppercase tracking-widest text-[var(--gt-ink-2)]">
                            Ticket {i + 1} · {u.ticket_type_name}
                          </div>
                          <button
                            type="button"
                            onClick={() => copyBuyerToAttendee(i)}
                            className="text-xs underline underline-offset-2 text-[var(--gt-ink-2)] shrink-0"
                            data-testid={`attendee-use-buyer-${i}`}
                          >
                            Use my details
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-3">
                          {guestConfig.name !== "off" && (
                            <div>
                              <label className="gt-form-label">
                                Name{" "}
                                <span className="text-[var(--gt-ink-3)] font-normal">
                                  ({guestConfig.name === "required" ? "required" : "optional"})
                                </span>
                              </label>
                              <input
                                className="gt-input"
                                value={attendees[i]?.name || ""}
                                onChange={(e) => setAttendeeField(i, "name", e.target.value)}
                                placeholder="Full name"
                                maxLength={120}
                                data-testid={`attendee-name-${i}`}
                              />
                              {attendeeErrors[`${i}-name`] && (
                                <p className="text-xs text-[var(--gt-accent)] mt-1" data-testid={`attendee-name-error-${i}`}>
                                  {attendeeErrors[`${i}-name`]}
                                </p>
                              )}
                            </div>
                          )}
                          {guestConfig.email !== "off" && (
                            <div>
                              <label className="gt-form-label">
                                Email{" "}
                                <span className="text-[var(--gt-ink-3)] font-normal">
                                  ({guestConfig.email === "required" ? "required" : "optional"})
                                </span>
                              </label>
                              <input
                                className="gt-input"
                                type="email"
                                inputMode="email"
                                value={attendees[i]?.email || ""}
                                onChange={(e) => setAttendeeField(i, "email", e.target.value)}
                                placeholder="guest@youraddress.com.au"
                                maxLength={254}
                                data-testid={`attendee-email-${i}`}
                              />
                              {attendeeErrors[`${i}-email`] && (
                                <p className="text-xs text-[var(--gt-accent)] mt-1" data-testid={`attendee-email-error-${i}`}>
                                  {attendeeErrors[`${i}-email`]}
                                </p>
                              )}
                            </div>
                          )}
                          {guestConfig.phone !== "off" && (
                            <div>
                              <label className="gt-form-label">
                                Mobile{" "}
                                <span className="text-[var(--gt-ink-3)] font-normal">
                                  ({guestConfig.phone === "required" ? "required" : "optional"})
                                </span>
                              </label>
                              <input
                                className="gt-input"
                                type="tel"
                                inputMode="tel"
                                value={attendees[i]?.phone || ""}
                                onChange={(e) => setAttendeeField(i, "phone", e.target.value)}
                                placeholder="0412 345 678"
                                maxLength={32}
                                data-testid={`attendee-phone-${i}`}
                              />
                              {attendeeErrors[`${i}-phone`] && (
                                <p className="text-xs text-[var(--gt-accent)] mt-1" data-testid={`attendee-phone-error-${i}`}>
                                  {attendeeErrors[`${i}-phone`]}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="consent-label">
                  <input
                    type="checkbox"
                    className="mt-1 accent-[var(--gt-accent)]"
                    checked={consented}
                    onChange={(e) => setConsented(e.target.checked)}
                    data-testid="consent-checkbox"
                  />
                  <span className="text-[var(--gt-ink-2)]">
                    I agree to the Getickets <a href="/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2">Terms &amp; Privacy Policy</a>, and consent to my details being shared with the event organiser.
                  </span>
                </label>

                {error && (
                  <div
                    className="text-sm text-[var(--gt-accent)] font-medium"
                    data-testid="checkout-error"
                    role="alert"
                    aria-live="assertive"
                  >
                    {error}
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="gt-btn-ghost text-sm"
                    disabled={submitting}
                    data-testid="cancel-checkout-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="gt-btn-primary"
                    disabled={submitting || (countdown && countdown.totalSec === 0)}
                    data-testid="complete-checkout-btn"
                  >
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    {isFree ? "Get my tickets" : (
                      <>
                        <CreditCard size={14} /> Continue to payment
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Order summary */}
            <aside className="lg:sticky lg:top-24 self-start">
              <div className="gt-card-hard bg-white">
                <div className="gt-label mb-4">Order summary</div>
                <div className="mb-4">
                  <div className="font-heading text-lg font-bold leading-tight" data-testid="summary-event-title">{session?.event_title}</div>
                  <div className="text-xs text-[var(--gt-ink-2)]">
                    {formatAuDate(session?.event_start_at, session?.event_timezone)}
                    <br />
                    {session?.event_venue_name}, {session?.event_state}
                  </div>
                </div>
                <div className="space-y-3 border-t border-[var(--gt-line)] pt-4" data-testid="summary-items">
                  {(session?.items || []).map((it) => (
                    <div key={it.id} className="flex items-center justify-between text-sm">
                      <div>
                        <div className="font-semibold">{it.ticket_type_name}</div>
                        <div className="text-xs text-[var(--gt-ink-2)]">× {it.quantity}</div>
                      </div>
                      <div className="font-mono font-bold">
                        {it.price_cents === 0 ? "Free" : centsToAUD(it.price_cents * it.quantity)}
                      </div>
                    </div>
                  ))}
                  {/* Sprint T1 — add-on (Extras) lines */}
                  {(session?.addon_items || []).map((a) => (
                    <div key={a.addon_id} className="flex items-center justify-between text-sm" data-testid={`summary-addon-${a.addon_id}`}>
                      <div>
                        <div className="font-semibold">
                          {a.name}
                          <span className="ml-1.5 text-[9px] uppercase tracking-widest px-1 py-0.5 border border-[var(--gt-line)] rounded-sm text-[var(--gt-ink-3)]">Extra</span>
                        </div>
                        <div className="text-xs text-[var(--gt-ink-2)]">× {a.quantity}</div>
                      </div>
                      <div className="font-mono font-bold">
                        {a.line_total_cents === 0 ? "Free" : centsToAUD(a.line_total_cents)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 border-t border-[var(--gt-line)] pt-4 space-y-1 text-sm text-[var(--gt-ink-2)]" data-testid="summary-breakdown">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span className="font-mono" data-testid="summary-subtotal">
                      {session?.gross_ticket_cents === 0 ? "A$0.00" : centsToAUD(session?.gross_ticket_cents || 0)}
                    </span>
                  </div>
                  {session?.addons_total_cents > 0 && (
                    <div className="flex justify-between">
                      <span>Extras</span>
                      <span className="font-mono" data-testid="summary-extras">{centsToAUD(session.addons_total_cents)}</span>
                    </div>
                  )}
                  {session?.donation_cents > 0 && (
                    <div className="flex justify-between text-xs">
                      <span>Incl. donation</span>
                      <span className="font-mono" data-testid="summary-donation">{centsToAUD(session.donation_cents)}</span>
                    </div>
                  )}
                  {session?.service_charge_paid_by === "customer" && session?.service_charge_cents > 0 ? (
                    <div>
                      <div className="flex justify-between">
                        <span className="flex items-center gap-1">
                          Service fee
                          <button
                            type="button"
                            onClick={() => setShowFeeInfo((v) => !v)}
                            aria-expanded={showFeeInfo}
                            aria-label="How is the service fee calculated?"
                            className="p-1.5 -my-1.5 text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)] transition-colors"
                            data-testid="service-fee-info-toggle"
                          >
                            <Info size={13} />
                          </button>
                        </span>
                        <span className="font-mono" data-testid="summary-service-charge">
                          {centsToAUD(session.service_charge_cents)}
                        </span>
                      </div>
                      {showFeeInfo && (
                        <div className="mt-1 text-[11px] text-[var(--gt-ink-3)]" data-testid="service-fee-info-detail">
                          Service fee is calculated as {session?.service_charge_rate_bps != null ? session.service_charge_rate_bps / 100 : 2.5}% + A$
                          {((session?.service_charge_per_ticket_cents ?? 30) / 100).toFixed(2)} per ticket.
                        </div>
                      )}
                    </div>
                  ) : session?.service_charge_paid_by === "included" && session?.ticket_subtotal_cents > 0 ? (
                    <div className="flex justify-between">
                      <span>Service fee</span>
                      <span data-testid="summary-service-charge">Included in ticket price</span>
                    </div>
                  ) : null}
                  {session?.gst_enabled === false && session?.ticket_subtotal_cents > 0 ? (
                    <div className="flex justify-between">
                      <span>Service fee GST</span>
                      <span data-testid="summary-gst">Not applied</span>
                    </div>
                  ) : (session?.gst_paid_by || session?.tax_paid_by) === "customer" && (session?.gst_amount_cents ?? session?.tax_cents) > 0 ? (
                    <div className="flex justify-between">
                      <span>Service fee GST</span>
                      <span className="font-mono" data-testid="summary-gst">
                        {centsToAUD(session?.gst_amount_cents ?? session?.tax_cents ?? 0)}
                      </span>
                    </div>
                  ) : (session?.gst_paid_by || session?.tax_paid_by) === "included" && session?.ticket_subtotal_cents > 0 ? (
                    <div className="flex justify-between">
                      <span>Service fee GST</span>
                      <span data-testid="summary-gst">Included in ticket price</span>
                    </div>
                  ) : null}
                </div>

                <div className="mt-4 border-t border-[var(--gt-line)] pt-4 flex items-center justify-between">
                  <div className="gt-label">Cost to buyer (AUD)</div>
                  <div className="font-heading text-2xl font-black tracking-tight" data-testid="summary-total">
                    {session?.amount_total_cents === 0
                      ? "A$0.00"
                      : centsToAUD(session?.amount_total_cents || 0)}
                  </div>
                </div>
                {isFree && (
                  <p className="mt-3 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] text-center">
                    Free event · no payment required
                  </p>
                )}
                {isPaid && (
                  <p className="mt-3 text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)] text-center">
                    Stripe · {session?.stripe_test_mode ? "Test mode" : "Secure payment"} · Card required
                  </p>
                )}
              </div>
            </aside>
          </div>
        )}
      </div>

      <footer className="bg-[var(--gt-ink)] text-[var(--gt-bg-marketing)] mt-16">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Logo inverse size="md" />
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-5">
            <ManageCookiesLink className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]" />
            <div className="text-xs tracking-[0.25em] uppercase text-[var(--gt-ink-3)]">
              © {new Date().getFullYear()} Getickets · AUD · GST-Ready
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
